import React from 'react';
import {Button} from 'react-bootstrap';
import {FmlxCheckBox, FmlxIcon} from 'fmlx-common-ui';
import {batch} from 'react-redux';
import axios from 'axios';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {
  SignalRHub,
  SignalREvent,
  ApiRouteMap,
  ViewMode,
  FastRobotErrorcode,
  CurrentStateEnum,
  Feedback,
  LicenseStatus,
  UserRoleLevel,
  SoftwareUpdateResult,
} from '../../constants/dataEnum';
import {hideLoading, showLoading, changeView, setDrawerVisibility, showSnackbar} from '../actions/appStateAction';
import {invokeAPI, callAPI, resolveIntl, setErrorCodeNumber} from './commonBusiness';
// eslint-disable-next-line import/no-cycle
import {deleteSession, resetState} from './sessionBusiness';
import {setIsDowngrading, setIsFinalizing, setIsUpdating} from '../actions/aboutStateAction';
import {hideModal, setModalContent, setModalInvalid, setModalValid, showModal} from '../actions/modalStateAction';
import {showFailedToUpdateSystemModal} from './modalStateBusinessAction';
import {getInstrumentStatus} from './instrumentBusiness';
import {showModalConnectionFailed} from './commonModalBusiness';
import {
  getSoftwareReloadAttemptFromLocalStorage,
  rebuildHubConnections,
  setSoftwareReloadAttemptFromLocalStorage,
} from './appBusiness';

const intl = resolveIntl();

// #region local storage

export const setVersionToLocalStorage = (dataVersion) => {
  const key = 'version';

  let version = {
    newVersion: dataVersion.softwareVersion,
    softwareUpdateResult: dataVersion.softwareUpdateResult?.result ?? null,
  };

  if (dataVersion.prevSoftwareVersion) {
    version = {...version, prevVersion: dataVersion.prevSoftwareVersion};
  }

  const versionStr = JSON.stringify(version);

  window.localStorage.setItem(key, versionStr);
};

export const getVersionFromLocalStorage = () => {
  const key = 'version';
  const value = window.localStorage.getItem(key);

  const version = JSON.parse(value);

  if (!version) {
    return null;
  }

  return version;
};

export const getSoftwareVersionFromLocalStorage = () => {
  const key = 'localVersion';
  const value = window.localStorage.getItem(key);

  return value;
};

export const setSoftwareVersionFromLocalStorage = (vers) => {
  const key = 'localVersion';
  window.localStorage.setItem(key, vers);
};

export const removeVersionFromLocalStorage = () => {
  const localVersion = getSoftwareVersionFromLocalStorage();
  const version = getVersionFromLocalStorage();
  const reloadAttempt = getSoftwareReloadAttemptFromLocalStorage();

  if (version?.newVersion === localVersion && Number(reloadAttempt) >= 1) {
    window.localStorage.removeItem('version');
    setSoftwareReloadAttemptFromLocalStorage(0);
  }
};

export const setSoftwareUpdateAvailableModalConfig = (val) => {
  const key = 'dontShowNewSoftwareUpdateModal';
  window.localStorage.setItem(key, val);
};

export const getSoftwareUpdateAvailableModalConfig = () => {
  const key = 'dontShowNewSoftwareUpdateModal';
  const value = window.localStorage.getItem(key);

  return value;
};

export const getWhatsNewModalConfig = () => {
  const key = 'dontShowWhatsNewModal';
  const value = window.localStorage.getItem(key);
  let config = {};

  if (value) {
    config = JSON.parse(value);
  }

  return config;
};

export const setWhatsNewModalConfig = (val) => {
  const key = 'dontShowWhatsNewModal';
  const prevConfig = getWhatsNewModalConfig();
  const newConfig = {...prevConfig, ...val};

  window.localStorage.setItem(key, JSON.stringify(newConfig));
};

export const removeWhatsNewModalConfig = () => {
  const key = 'dontShowWhatsNewModal';
  window.localStorage.removeItem(key);
};

export const setRollbackVersionToLocalStorage = (dataVersion) => {
  const key = 'rollbackVersion';
  const rollbackVersion = {
    [dataVersion.softwareVersion]: dataVersion.softwareUpdateResult?.dateTime ?? '',
  };
  const rollbackVersionString = JSON.stringify(rollbackVersion);

  window.localStorage.setItem(key, rollbackVersionString);
};

export const getRollbackVersionFromLocalStorage = () => {
  const key = 'rollbackVersion';
  const value = window.localStorage.getItem(key);
  const version = JSON.parse(value);

  if (!version) {
    return null;
  }

  return version;
};

// #endregion local storage

export const uploadPackage = (files) => async (dispatch, getState) => {
  const state = getState();
  const {configState} = state;

  const formData = new FormData();
  const file = files[0];
  formData.append('files', file, file.filename);

  const onUploadProgress = (progressEvent) => {
    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);

    console.log('onUploadProgress', percentCompleted);
    dispatch(
      showLoading(intl.formatMessage({id: 'about.updaterLoading.uploadFile.text'}, {percentage: percentCompleted})),
    );
  };

  const response = await callAPI(
    'POST',
    configState.hubHost.urlInstrumentControlLocation,
    `SoftwareUpdater/${ApiRouteMap.UPLOAD_UPDATE_PACKAGE}`,
    formData,
    null,
    null,
    onUploadProgress,
  );

  console.log('uploadPackage', response);

  return response;
};

export const multiUploadPackage = (files) => async (dispatch, getState) => {
  const state = getState();
  const {configState} = state;

  const file = files[0];
  const fileSize = file.size;
  const chunkSize = 10000000;

  let start = 0;
  let end = chunkSize;

  const chunks = [];

  while (start < fileSize) {
    const chunk = file.slice(start, end);
    chunks.push(chunk);

    start = end;
    end = start + chunkSize;
  }

  let response;

  response = await dispatch(invokeAPI(SignalRHub.SOFTWARE_UPDATER, SignalREvent.MULTI_UPLOAD_UPDATE_PACKAGE_START));

  if (response !== FastRobotErrorcode.NoError) {
    return {
      errorCode: response,
    };
  }

  for (let i = 0, ii = chunks.length; i < ii; i += 1) {
    const e = chunks[i];

    const formData = new FormData();
    formData.append('files', e, i);
    formData.append('index', i);

    // eslint-disable-next-line no-await-in-loop
    response = await callAPI(
      'POST',
      configState.hubHost.urlInstrumentControlLocation,
      `SoftwareUpdater/${ApiRouteMap.MULTI_UPLOAD_UPDATE_PACKAGE}`,
      formData,
      null,
      null,
    );

    if (response.errorCode !== FastRobotErrorcode.NoError && response.errorCode !== 0) {
      return response;
    }

    const percentCompleted = Math.round(((i + 1) * 100) / chunks.length);

    console.log('onUploadProgress', percentCompleted);
    dispatch(
      showLoading(intl.formatMessage({id: 'about.updaterLoading.uploadFile.text'}, {percentage: percentCompleted})),
    );
  }

  dispatch(showLoading(intl.formatMessage({id: 'about.updaterLoading.uploadFile.text2'})));

  response = await dispatch(invokeAPI(SignalRHub.SOFTWARE_UPDATER, SignalREvent.MULTI_UPLOAD_UPDATE_PACKAGE_COMPLETE));

  return {
    errorCode: response,
  };
};

export const prepareSoftwareUpdate = () => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;

  const response = await dispatch(invokeAPI(SignalRHub.SOFTWARE_UPDATER, SignalREvent.PREPARE_SOFTWARE_UPDATE, userId));

  console.log('prepareSoftwareUpdate', response);

  if (response === FastRobotErrorcode.NoError) {
    dispatch(setIsUpdating(true));
  }

  return response;
};

export const cancelFinalizeSoftwareUpdate = () => async (dispatch) => {
  const response = await dispatch(invokeAPI(SignalRHub.SOFTWARE_UPDATER, SignalREvent.CANCEL_FINALIZE_SOFTWARE_UPDATE));

  console.log(SignalREvent.CANCEL_FINALIZE_SOFTWARE_UPDATE, response);

  return response;
};

export const finalizeSoftwareUpdate = () => async (dispatch) => {
  await dispatch(showLoading(intl.formatMessage({id: 'about.updaterLoading.systemRestart.text'})));

  const response = await dispatch(invokeAPI(SignalRHub.SOFTWARE_UPDATER, SignalREvent.FINALIZE_SOFTWARE_UPDATE));

  console.log('finalizeSoftwareUpdate', response);

  if (response === FastRobotErrorcode.NoError) {
    dispatch(setIsFinalizing(true));
    dispatch(setIsUpdating(false));

    dispatch(rebuildHubConnections());
  } else {
    dispatch(hideLoading());
    dispatch(
      showFailedToUpdateSystemModal(
        () => dispatch(finalizeSoftwareUpdate()),
        () => dispatch(cancelFinalizeSoftwareUpdate()),
      ),
    );
  }
};

export const getSoftwareUpdateStatus = () => async (dispatch) => {
  let response;

  try {
    response = await dispatch(invokeAPI(SignalRHub.SOFTWARE_UPDATER, SignalREvent.GET_SOFTWARE_UPDATE_STATUS));
  } catch (err) {
    console.log(err);
  }

  console.log('getSoftwareUpdateStatus', response);

  return response;
};

export const showUpdateCompleteLoadingScreen = () => (dispatch) => {
  const buttonStyle = {
    marginTop: '50px',
    fontSize: 14,
    padding: '4px 12px',
    height: '34px',
    borderRadius: '5px',
    cursor: 'pointer',
  };
  const loadingElement = (
    <div>
      <div style={{marginTop: '60px'}}>
        <FormattedHTMLMessage id="about.updaterLoading.updateCompleted.text" />
      </div>
      <Button
        className="button-primary"
        style={buttonStyle}
        onClick={async () => {
          await dispatch(hideLoading());
          dispatch(finalizeSoftwareUpdate());
        }}
      >
        {intl.formatMessage({id: 'about.updaterLoading.updateCompleted.button'})}
      </Button>
    </div>
  );

  dispatch(showLoading(loadingElement));
};

export const kickUser =
  (executorUserId, showMessage = true) =>
  (dispatch, getState) => {
    const state = getState();
    const {appState, sessionState} = state;
    const {user} = sessionState;
    const {userId} = user;

    if (appState.viewMode === ViewMode.LOGIN) {
      return;
    }

    if (userId === executorUserId) {
      return;
    }

    batch(() => {
      dispatch(hideModal());
      dispatch(setDrawerVisibility(false));
      dispatch(showLoading(intl.formatMessage({id: 'login.LoginBoxContainer.sessionTakenLoading'})));

      dispatch(resetState());
      dispatch(changeView(ViewMode.LOGIN));

      setTimeout(() => {
        dispatch(hideLoading());
        if (showMessage)
          dispatch(showSnackbar(Feedback.ERROR, intl.formatMessage({id: 'about.updaterNotification.kickedOut'})));
      }, 2000);
    });
  };

export const downgradeSoftware = () => async (dispatch, getState) => {
  const {sessionState} = getState();
  const {userId} = sessionState.user;

  dispatch(setIsDowngrading(true));

  const response = await dispatch(invokeAPI(SignalRHub.SOFTWARE_UPDATER, SignalREvent.DOWNGRADE_SOFTWARE, userId));

  console.log(SignalREvent.DOWNGRADE_SOFTWARE, response);

  if (response !== FastRobotErrorcode.NoError) {
    return response;
  }

  dispatch(rebuildHubConnections());

  return response;
};

/* obsolete */
export const getSoftwareVersion = () => async (dispatch, getState) => {
  const state = getState();
  const {configState} = state;

  const response = await callAPI(
    'GET',
    configState.hubHost.urlInstrumentControlLocation,
    `SoftwareUpdater/${ApiRouteMap.GET_FAST_INFO}`,
    null,
    null,
    null,
    null,
  );

  return response;
};

export const validateSoftwareUpdateResult = (softwareUpdateInfo) => () => {
  try {
    const {softwareVersion: serverVersion = null, softwareUpdateResult = {}} = softwareUpdateInfo || {};
    const {result = null} = softwareUpdateResult;
    const localVersion = getSoftwareVersionFromLocalStorage();

    return {
      isSoftwareUpToDate: serverVersion === localVersion,
      isSoftwareUpdateFailed: result === SoftwareUpdateResult.FAILED,
      errorDuringValidation: serverVersion === null,
    };
  } catch (error) {
    console.error(error?.message ?? error);

    return {isSoftwareUpToDate: false, isSoftwareUpdateFailed: false, errorDuringValidation: true};
  }
};

export const reloadAfterUpdate = (softwareUpdateInfo) => (dispatch, getState) => {
  const {sessionState} = getState();
  const {userId} = sessionState.user;
  const serverVersion = softwareUpdateInfo?.softwareVersion;
  const reloadAttempt = getSoftwareReloadAttemptFromLocalStorage();

  if (Number(reloadAttempt) >= 1) {
    dispatch(deleteSession(userId));
    dispatch(resetState());
    setVersionToLocalStorage(softwareUpdateInfo);
    setSoftwareVersionFromLocalStorage(serverVersion);
    removeWhatsNewModalConfig();
  }

  console.log('Software is Updated, reload');
  setSoftwareReloadAttemptFromLocalStorage();
  window.location.reload(true);
};

let updaterFileName = '';
let updaterPackageError = '';

const fileUploadHandler = (fileList, showSoftwareUpdateModalCallback) => async (dispatch, getState) => {
  const {licenseState} = getState();
  const file = fileList[0];
  let isUploaded = false;

  updaterFileName = file.name;
  dispatch(hideModal());
  dispatch(showLoading(intl.formatMessage({id: 'about.updaterLoading.uploadFile.text'}, {percentage: 0})));
  const response = await dispatch(multiUploadPackage(fileList));
  await dispatch(setErrorCodeNumber(response.errorCode));

  if (response.errorCode === FastRobotErrorcode.NoError || response.errorCode === 0) {
    updaterPackageError = '';
    isUploaded = true;
  } else if (
    response.errorCode === FastRobotErrorcode.INVALID_UPDATE_PACKAGE &&
    licenseState.status === LicenseStatus.PERMANENT_EXPIRED
  ) {
    updaterPackageError = intl.formatMessage({id: 'about.updaterModal.errorMessage.invalidHotfix'});
  } else if (response.errorCode === FastRobotErrorcode.INVALID_UPDATE_PACKAGE) {
    updaterPackageError = intl.formatMessage({id: 'about.updaterModal.errorMessage.invalidFile'});
  } else if (response.errorCode === FastRobotErrorcode.FAILED_TO_UPLOAD_UPDATE_PACKAGE) {
    updaterPackageError = intl.formatMessage({id: 'about.updaterModal.errorMessage.uploadFailed'});
  } else if (response.errorCode === FastRobotErrorcode.OLDER_UPDATE_PACKAGE) {
    updaterPackageError = intl.formatMessage({id: 'about.updaterModal.errorMessage.olderVersion'});
  } else if (response.errorCode === FastRobotErrorcode.INVALID_LICENSE_MAINTENANCE_EXPIRED) {
    updaterPackageError = intl.formatMessage({id: 'about.updaterModal.errorMessage.maintenanceExpired'});
  }

  dispatch(hideLoading());
  dispatch(showSoftwareUpdateModalCallback());
  if (isUploaded) dispatch(setModalValid());
};

const updateHandler = () => async (dispatch) => {
  updaterFileName = '';
  updaterPackageError = '';
  dispatch(showLoading(intl.formatMessage({id: 'instrument.loading.checkConnection'})));
  const status = await dispatch(getInstrumentStatus());
  dispatch(hideLoading());

  if (!status) {
    dispatch(showModalConnectionFailed());
    return;
  }

  if (status?.currentState !== CurrentStateEnum.IDLE) {
    dispatch(
      showSnackbar(
        Feedback.ERROR,
        intl.formatMessage({
          id: 'about.updaterNotification.runningProtocol',
        }),
      ),
    );
    return;
  }

  dispatch(showLoading(<FormattedHTMLMessage id="about.updaterLoading.updating.text" values={{percentage: 0}} />));
  const response = await dispatch(prepareSoftwareUpdate());
  if (response === FastRobotErrorcode.NoError) {
    dispatch(showUpdateCompleteLoadingScreen());
  } else {
    dispatch(hideLoading());
    dispatch(showFailedToUpdateSystemModal(() => dispatch(updateHandler())));
  }
};

export const showSoftwareUpdateModal = () => async (dispatch) => {
  dispatch(setModalInvalid());
  dispatch(showLoading(intl.formatMessage({id: 'instrument.loading.checkConnection'})));
  const response = await dispatch(getInstrumentStatus());
  dispatch(hideLoading());

  if (!response) {
    dispatch(setModalValid());
    dispatch(showModalConnectionFailed());
    return;
  }

  if (response?.currentState !== CurrentStateEnum.IDLE) {
    dispatch(
      showSnackbar(
        Feedback.ERROR,
        intl.formatMessage({
          id: 'about.updaterNotification.runningProtocol',
        }),
      ),
    );
    dispatch(setModalValid());
    return;
  }

  const content = (
    <div className="software-update-modal">
      <p style={{marginTop: 0}}>{intl.formatMessage({id: 'about.updaterModal.content'})}</p>
      <div className="upload-file-button">
        <input
          type="file"
          className="upload-file-button__input"
          id="updater-file"
          accept=".zip"
          onChange={(event) => {
            dispatch(fileUploadHandler(event.target.files, showSoftwareUpdateModal));
          }}
        />
        <label htmlFor="updater-file" className="upload-file-button__label">
          <span className="upload-file-button__icon icon-uploadprotocol_contextualmenu" />
          {intl.formatMessage({id: 'about.updaterModal.uploadButton'})}
        </label>
        <span className="upload-file-button__filename">{updaterFileName}</span>
      </div>
      {updaterPackageError && (
        <div className="upload-file-button__error">
          <FmlxIcon name="Warning" size="xs" customColor="red" />
          <span>{updaterPackageError}</span>
        </div>
      )}
    </div>
  );

  dispatch(
    showModal(
      intl.formatMessage({id: 'about.updaterModal.title'}),
      content,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(updateHandler());
            dispatch(setModalValid());
          },
          text: intl.formatMessage({id: 'about.updaterModal.firstButton'}),
          setButtonValidityByState: true,
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            updaterFileName = '';
            updaterPackageError = '';
            dispatch(setModalValid());
          },
          text: intl.formatMessage({id: 'about.updaterModal.secondButton'}),
        },
      ],
      () => dispatch(hideModal()),
      '',
      true,
    ),
  );
};

export const showIncompatibleVisionVersionModal = () => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({id: 'modal.incompatibleVisionVersion.title'}),
      intl.formatMessage({id: 'modal.incompatibleVisionVersion.text'}),
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(showSoftwareUpdateModal());
          },
          text: intl.formatMessage({
            id: 'modal.incompatibleVisionVersion.firstButton',
          }),
        },
      ],
      () => dispatch(hideModal()),
      '',
      false,
    ),
  );
};

export const showNewSoftwareUpdateAvailableInfoModal =
  (isLater = false) =>
  (dispatch) => {
    let textID = 'modal.newSoftwareUpdateAvailableInfo.text';

    if (isLater) {
      textID = 'modal.newSoftwareUpdateAvailableInfo.text.later';
    }
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.newSoftwareUpdateAvailableInfo.title',
        }),
        <FormattedHTMLMessage id={textID} />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'modal.newSoftwareUpdateAvailableInfo.firstButton',
            }),
          },
        ],
        dispatch(hideModal()),
        '',
        true,
      ),
    );
  };

export const showNewSoftwareUpdateAvailableModal =
  (link = '', showRadioButton = true) =>
  async (dispatch) => {
    let downloadLink;
    if (link) {
      downloadLink = link;
    } else {
      const res = await axios.get('newSoftwareRelease.json');
      if (!res?.data?.link) {
        return;
      }

      downloadLink = res?.data?.link;
    }
    let modalCheck = false;
    const content = (
      <>
        <span>{intl.formatMessage({id: 'modal.newSoftwareUpdateAvailable.text'})}</span>
        &nbsp;
        <a href="https://www.formulatrix.com/downloads/apps/FAST/release_notes.html" target="_blank" rel="noreferrer">
          <FormattedHTMLMessage id="modal.newSoftwareUpdateAvailable.link" />
        </a>
        {showRadioButton && (
          <FmlxCheckBox
            onChange={() => {
              modalCheck = !modalCheck;
            }}
            label={intl.formatMessage({
              id: 'modal.newSoftwareUpdateAvailable.radioButton',
            })}
          />
        )}
      </>
    );
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.newSoftwareUpdateAvailable.title',
        }),
        content,
        [
          {
            className: 'btn-lg',
            style: 'secondary',
            handlerClick: () => {
              dispatch(hideModal());
              if (showRadioButton) {
                setSoftwareUpdateAvailableModalConfig(modalCheck);
                dispatch(showNewSoftwareUpdateAvailableInfoModal(true));
              }
            },
            text: intl.formatMessage({
              id: 'modal.newSoftwareUpdateAvailable.firstButton',
            }),
          },
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
              window.open(downloadLink, '_blank');
              window.focus();
              if (showRadioButton) {
                setSoftwareUpdateAvailableModalConfig(modalCheck);
              }
              dispatch(showNewSoftwareUpdateAvailableInfoModal());
            },
            text: intl.formatMessage({
              id: 'modal.newSoftwareUpdateAvailable.secondButton',
            }),
          },
        ],
        dispatch(hideModal()),
        '',
        true,
      ),
    );
  };

const isNewSoftwareUpdate = (newVersion) => {
  const softwareVersion = getSoftwareVersionFromLocalStorage();

  if (!softwareVersion) {
    setSoftwareVersionFromLocalStorage(newVersion);
    return true;
  }

  // second arg could be git branch versioning
  const [localVersion] = softwareVersion.split('-');
  const currentVersionNumber = localVersion.split('.');

  const newVersionNumber = newVersion.split('.');

  let isNewSoftware = false;
  const newMajorVersion = Number(newVersionNumber[0]);
  const newMinorVersion = Number(newVersionNumber[1]);
  const newHotfixVersion = Number(newVersionNumber[2]);

  const currentMajorVersion = Number(currentVersionNumber[0]);
  const currentMinorVersion = Number(currentVersionNumber[1]);
  const currentHotfixVersion = Number(currentVersionNumber[2]);

  if (newMajorVersion !== currentMajorVersion) {
    isNewSoftware = true;
  } else if (newMajorVersion === currentMajorVersion) {
    if (newMinorVersion !== currentMinorVersion) {
      isNewSoftware = true;
    } else if (newMinorVersion === currentMinorVersion) {
      if (newHotfixVersion !== currentHotfixVersion) {
        isNewSoftware = true;
      }
    }
  }

  return isNewSoftware;
};

export const getCurrentVersionData = async () => {
  const softwareVersion = getSoftwareVersionFromLocalStorage();

  const res = await axios.get(`newRelease.json?version=${softwareVersion}`);

  return res;
};

export const showWhatsNewInFASTModal =
  (onAfterClose = null) =>
  async (dispatch, getState) => {
    const {sessionState, userManagementState, appState, licenseState} = getState();
    const {viewMode} = appState;

    if (viewMode === ViewMode.RUNTIME) {
      return false;
    }

    const {userId} = sessionState.user;
    const {availableRoleRights} = userManagementState;
    const userRoleLevel = availableRoleRights.map((e) => e.roleLevel)[0];
    const {isTessaraInstrument} = licenseState;

    if (userRoleLevel === UserRoleLevel.OPERATOR) {
      return false;
    }

    const res = await getCurrentVersionData();
    const newVersion = res?.data?.version;

    const isNewSoftware = isNewSoftwareUpdate(newVersion);

    if (isNewSoftware) {
      removeWhatsNewModalConfig();
    }

    const config = getWhatsNewModalConfig();
    const dontShow = config[userId];

    if (dontShow) {
      console.log("doesn't show what's new modal from config");
      return false;
    }

    let newFeature = [];
    let newFeatureTitle = '';
    let newFeatureSubTitle = '';

    let bugFixes = [];
    let bugFixesTitle = '';
    let bugFixesSubTitle = '';

    if (res.data) {
      if (isTessaraInstrument) {
        newFeature = [...newFeature, ...res.data.features.tessaraItems];
        bugFixes = [...bugFixes, ...res.data.bugFixes.tessaraItems];
      }

      newFeature = [...newFeature, ...res.data.features.items];
      newFeatureTitle = res.data.features.title;
      newFeatureSubTitle = res.data.features.subTitle;

      bugFixes = [...bugFixes, ...res.data.bugFixes.items];
      bugFixesTitle = res.data.bugFixes.title;
      bugFixesSubTitle = res.data.bugFixes.subTitle;

      newFeature = newFeature.filter((e) => e.title || e.detail);
      bugFixes = bugFixes.filter((e) => e);
    }

    if ((!newFeature.length || (!newFeature[0]?.title && !newFeature[0]?.detail)) && !bugFixes.length) {
      console.log("doesn't show what's new modal if json file empty");
      return false;
    }

    let isChecked = false;
    let text = '';

    if (newFeature[0]?.title && newFeature[0]?.detail && bugFixes.length) {
      text = intl.formatMessage({id: 'modal.whatsNewInFast.text.newFeatureAndBugFixes'});
      text = (
        <FormattedHTMLMessage id="modal.whatsNewInFast.text.newFeatureAndBugFixes" values={{version: newVersion}} />
      );
    } else if (newFeature[0]?.title && newFeature[0]?.detail && !bugFixes.length) {
      text = <FormattedHTMLMessage id="modal.whatsNewInFast.text.newFeature" values={{version: newVersion}} />;
    }
    if ((!newFeature.length || (!newFeature[0]?.title && !newFeature[0]?.detail)) && bugFixes.length) {
      text = <FormattedHTMLMessage id="modal.whatsNewInFast.text.bugFixes" values={{version: newVersion}} />;
    }

    const getContent = (isCheckedValue) => {
      const onChangeHandler = (e) => {
        dispatch(setModalContent(getContent(e.target.checked)));
        isChecked = !isChecked;
      };

      const drawDetail = (e) => {
        if (e.title) {
          if (e.detail && !Array.isArray(e.detail)) {
            return (
              <>
                <li key={e.title} style={{fontWeight: 'bold'}}>
                  <div>
                    <b>{e.title}</b>
                  </div>
                </li>
                <span key={e.detail}>{e.detail}</span>
              </>
            );
          }
          return (
            <>
              <li key={e.title} style={{fontWeight: 'bold'}}>
                <div>
                  <b>{e.title}</b>
                </div>
              </li>
              <ul style={{marginLeft: '-22px'}}>
                {e.detail.map((detail) => (
                  <li>
                    <span key={detail}>{detail}</span>
                  </li>
                ))}
              </ul>
            </>
          );
        }

        return null;
      };
      return (
        <>
          <div style={{maxHeight: 400, overflow: 'scroll'}}>
            <div>{text}</div>
            &nbsp;
            {newFeature.length ? (
              <div>
                <b>{newFeatureTitle}</b>
                &emsp;
                <p style={{margin: 0}}>{newFeatureSubTitle}</p>
                <ol style={{margin: 5, lineHeight: 1.5}}>{newFeature.map((e) => drawDetail(e))}</ol>
              </div>
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <></>
            )}
            {bugFixes.length ? (
              <div>
                <b>{bugFixesTitle}</b>
                <p style={{margin: 0}}>{bugFixesSubTitle}</p>
                <ul style={{margin: 5, lineHeight: 1.5}}>
                  {bugFixes.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </div>
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <></>
            )}
          </div>
          <FmlxCheckBox
            onChange={onChangeHandler}
            label={intl.formatMessage({
              id: 'modal.whatsNewInFast.radioButton',
            })}
            checked={isCheckedValue}
          />
        </>
      );
    };
    dispatch(
      showModal(
        intl.formatMessage({id: 'modal.whatsNewInFast.title'}),
        getContent(false),
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              setWhatsNewModalConfig({[userId]: isChecked});
              dispatch(hideModal());
              if (typeof onAfterClose === 'function') onAfterClose();
            },
            text: intl.formatMessage({
              id: 'modal.whatsNewInFast.firstButton',
            }),
          },
        ],
        () => {
          setWhatsNewModalConfig({[userId]: isChecked});
          dispatch(hideModal());
          if (typeof onAfterClose === 'function') onAfterClose();
        },
        '',
        false,
      ),
    );

    return true;
  };

export const getNewSoftwareUpdate = () => async (dispatch) => {
  const dontShowMessage = getSoftwareUpdateAvailableModalConfig();

  if (dontShowMessage === 'true') {
    return;
  }

  const res = await axios.get('newSoftwareRelease.json');

  console.log('new software release', res);

  if (!res?.data?.version) {
    return;
  }

  const isNewSoftware = isNewSoftwareUpdate(res?.data?.version);

  if (isNewSoftware) dispatch(showNewSoftwareUpdateAvailableModal(false, res?.data?.link));
};

const isRollbackNotified = (softwareVersionInfo) => {
  const {softwareVersion = null, softwareUpdateResult = {}} = softwareVersionInfo || {};
  const {dateTime = null} = softwareUpdateResult;
  const rollbackSoftwareInfo = getRollbackVersionFromLocalStorage();

  return (
    rollbackSoftwareInfo && rollbackSoftwareInfo[softwareVersion] && rollbackSoftwareInfo[softwareVersion] === dateTime
  );
};

export const handleSoftwareUpdateFailed = (serverVersionInfo) => (dispatch) => {
  const showFailedSnackbar = () => {
    const {softwareVersion: currentVersion = ''} = serverVersionInfo || {};

    dispatch(
      showSnackbar(
        Feedback.ERROR,
        intl.formatMessage({id: 'about.updaterNotification.updateFailed.message'}, {currentVersion}),
        null,
        true,
        true,
        null,
        '',
        intl.formatMessage({id: 'about.updaterNotification.updateFailed.title'}),
      ),
    );
  };

  if (!isRollbackNotified(serverVersionInfo)) {
    showFailedSnackbar();
    setRollbackVersionToLocalStorage(serverVersionInfo);
    setSoftwareReloadAttemptFromLocalStorage(0);
  }
};

export const handleSoftwareUpdateSuccess = (serverVersionInfo, isSoftwareUpToDate) => (dispatch) => {
  const localVersionInfo = getVersionFromLocalStorage();

  const showSuccessSnackbar = () => {
    const {prevVersion = '', newVersion: currentVersion = ''} = localVersionInfo;

    const showDowngradeSWMessage = !prevVersion && currentVersion;
    const showUpdateSWMessage = prevVersion && currentVersion;

    if (showDowngradeSWMessage) {
      dispatch(
        showSnackbar(
          Feedback.SUCCESS,
          intl.formatMessage({id: 'about.updaterNotification.downgradeSuccess'}, {currentVersion}),
        ),
      );
    } else if (showUpdateSWMessage) {
      dispatch(
        showSnackbar(
          Feedback.SUCCESS,
          intl.formatMessage({id: 'about.updaterNotification.updateSuccess'}, {prevVersion, currentVersion}),
        ),
      );
    }
  };

  if (isSoftwareUpToDate && localVersionInfo) {
    showSuccessSnackbar();
    removeVersionFromLocalStorage();
  } else if (!isSoftwareUpToDate) {
    // This function is called to set local storage variables related with software update info.
    dispatch(reloadAfterUpdate(serverVersionInfo));
  }
};
